import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageContainer from '../components/layout/PageContainer';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { ArrowLeftIcon, ClockIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const TransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshToken } = useAuth();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchTransactions = async () => {
      if (!user?.uid) return;
      
      try {
        setLoading(true);
        setError(null);

        // Use existing token first
        let token = user.token || undefined;
        
        // Only refresh if no token exists
        if (!token) {
          try {
            const newToken = await refreshToken();
            if (newToken) {
              token = newToken;
            }
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError);
            // If refresh fails, try to continue with existing token
            if (!token) {
              throw new Error('Failed to get token');
            }
          }
        }

        const response = await fetch('https://getusertransactions-b7jrylvg3a-uc.a.run.app', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            userId: user.uid
          })
        });

        if (!response.ok) {
          if (response.status === 401) {
            // If unauthorized, try to refresh token once
            try {
              const newToken = await refreshToken();
              if (!newToken) {
                throw new Error('Failed to refresh token');
              }
              // Retry the request with new token
              const retryResponse = await fetch('https://getusertransactions-b7jrylvg3a-uc.a.run.app', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${newToken}`
                },
                body: JSON.stringify({
                  userId: user.uid
                })
              });
              if (!retryResponse.ok) {
                throw new Error('Failed to fetch transactions after token refresh');
              }
              const data = await retryResponse.json();
              if (isMounted) {
                const transactionsData = Array.isArray(data) ? data : data.transactions || [];
                setTransactions(transactionsData);
              }
              return;
            } catch (retryError) {
              throw new Error('Failed to fetch transactions');
            }
          }
          throw new Error('Failed to fetch transactions');
        }

        const data = await response.json();
        console.log('Transactions response:', data);

        if (isMounted) {
          const transactionsData = Array.isArray(data) ? data : data.transactions || [];
          setTransactions(transactionsData);
        }
      } catch (error) {
        console.error('Error fetching transactions:', error);
        if (isMounted) {
          setError('Erro ao carregar transações. Tente novamente mais tarde.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTransactions();

    return () => {
      isMounted = false;
    };
  }, [user]);

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </motion.button>
          <h1 className="text-2xl font-bold text-white">Transações</h1>
        </div>

        {/* Transactions List */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="text-center text-red-400 py-8">
              {error}
            </div>
          ) : transactions.length > 0 ? (
            transactions.map((transaction) => (
              <motion.div
                key={transaction.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white/10 backdrop-blur-lg rounded-xl p-4"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-medium">
                      {transaction.details || transaction.business}
                    </h3>
                    <div className="flex items-center gap-2 text-white/60 text-sm">
                      <ClockIcon className="h-4 w-4" />
                      {new Date(transaction.createdAt || transaction.date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-semibold ${
                        (transaction.type === 'earn' || transaction.type === 'earned')
                          ? 'text-green-400'
                          : 'text-red-400'
                      }`}
                    >
                      {(transaction.type === 'earn' || transaction.type === 'earned') ? '+' : '-'}
                      {transaction.amount || transaction.points}
                    </span>
                    <ArrowRightIcon className="h-5 w-5 text-white/60" />
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="text-center text-white/60 py-8">
              Nenhuma transação encontrada
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default TransactionsPage; 